import { createModel } from "@rematch/core";
import type { RootModel } from ".";
import { Snapshot, SnapshotStatus, SnapshotSubmitForm } from "../../types";
import axios from "axios";
import { API_ERRORS, API_NOT_WORKING } from "../../config/constants";

type BalanceData = {
  wallet: string;
  balance: string;
  type: string;
};

interface SnapshotApiResponse {
  snapshotStatus: string;
  results: BalanceData[];
  statusCode?: number;
}

interface SnapshotModel {
  snapshotData: Snapshot[] | undefined;
  snapshotStatus: SnapshotStatus | undefined;
  snapshotError: string | undefined;
  currentSnapshotId: string | undefined;
  processing: boolean;
}

export const snapshotModel = createModel<RootModel>()({
  state: {
    snapshotData: [],
    snapshotStatus: undefined,
    snapshotError: undefined,
    currentSnapshotId: "",
    processing: false,
  } as SnapshotModel,
  reducers: {
    setSnapshotData(state, payload: Snapshot[] | undefined) {
      return { ...state, snapshotData: payload };
    },
    setCurrentSnapshotId(state, payload: string) {
      return { ...state, currentSnapshotId: payload };
    },
    clearSnapshotData(state) {
      return { ...state, snapshotData: [] };
    },
    setIsProcessing(state, payload: boolean) {
      return { ...state, processing: payload };
    },
    setSnapshotStatus(state, payload: SnapshotStatus) {
      return { ...state, snapshotStatus: payload };
    },
    setSnapshotError(state, payload: string) {
      return { ...state, snapshotError: payload };
    },

    resetState(state) {
      const defaultState = {
        snapshotData: [],
        snapshotStatus: undefined,
        currentSnapshotId: "",
        processing: false,
      };
      return { ...state, ...defaultState };
    },
  },
  effects: (dispatch) => ({
    async checkSnapshotId(snapshotId: string) {
      try {
        const response = await axios.get<SnapshotApiResponse>(
          `${window._env_.SNAPSHOT_API}/snapshot/${snapshotId}`
        );
        const { data } = response;

        if (data.snapshotStatus === SnapshotStatus.NOT_FOUND) {
          return false;
        }

        return true;
      } catch (error: any) {
        if (error.response) {
          const statusCode: number = error.response.data.statusCode;
          if (statusCode === 429) {
            // in this case we return true, as we are rate limited so we will check later.
            return true;
          }
        }
        return false;
      }
    },
    async checkSnapshotStatus(snapshotId: string) {
      dispatch.snapshotModel.setSnapshotError("");
      try {
        const response = await axios.get<SnapshotApiResponse>(
          `${window._env_.SNAPSHOT_API}/snapshot/${snapshotId}`
        );
        const { data } = response;
        dispatch.snapshotModel.setIsProcessing(true);

        dispatch.snapshotModel.setSnapshotStatus(
          data.snapshotStatus as SnapshotStatus
        );

        if (data.snapshotStatus === SnapshotStatus.COMPLETED) {
          dispatch.snapshotModel.setSnapshotData(data.results);
          dispatch.snapshotModel.setIsProcessing(false);
        }
      } catch (error: any) {
        if (error.response) {
          const statusCode: number = error.response.data.statusCode;
          if (statusCode === 500) {
            dispatch.snapshotModel.setSnapshotStatus(SnapshotStatus.ERROR);
          }

          dispatch.snapshotModel.setIsProcessing(false);
        }
      }
    },

    async submitSnapshot(formData: SnapshotSubmitForm) {
      try {
        dispatch.snapshotModel.setSnapshotError("");
        const response = await axios.post<{
          id: string;
        }>(`${window._env_.SNAPSHOT_API}/snapshot/`, formData, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        const { data } = response;
        if (data.id) {
          dispatch.snapshotModel.setCurrentSnapshotId(data.id);
          dispatch.snapshotModel.setIsProcessing(true);
        }
      } catch (error: any) {
        if (error.response) {
          const message: string = error.response.data.message;
          const multipleExist = API_ERRORS.some((value) => {
            return message.toLowerCase().includes(value.toLowerCase());
          });
          if (!multipleExist) {
            dispatch.snapshotModel.setSnapshotError(API_NOT_WORKING);
          }
          dispatch.snapshotModel.setSnapshotError(message);
        }
      }
    },
  }),
});
